export interface Recipient {
  contactId: number;
  contactName: string;
  address: string;
}

export const recipientKey = (contactId: number, address: string) =>
  `${contactId}:${address.toLowerCase()}`;

export interface MultiRecipientControls {
  extraRecipients: Recipient[];
  onAddRecipient: (recipient: Recipient) => void;
  onRemoveRecipient: (recipient: Recipient) => void;
  primaryExcluded: boolean;
  onPrimaryExcludedChange: (excluded: boolean) => void;
}

interface DocNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const hasLinkChip = (doc: DocNode | null | undefined): boolean => {
  if (!doc) return false;
  if (doc.type === "mention" && (doc.attrs ?? {}).linkType) return true;
  return (doc.content ?? []).some(hasLinkChip);
};

export const docHasLinkChip = (doc: unknown): boolean =>
  hasLinkChip(doc as DocNode | null | undefined);

const hasNameChip = (doc: DocNode | null | undefined): boolean => {
  if (!doc) return false;
  if (doc.type === "mention" && !(doc.attrs ?? {}).linkType) return true;
  return (doc.content ?? []).some(hasNameChip);
};

export const docHasNameChip = (doc: unknown): boolean =>
  hasNameChip(doc as DocNode | null | undefined);

const nodeToText = (node: DocNode, values: Record<string, string>): string => {
  if (node.type === "text") return node.text ?? "";
  if (node.type === "mention") {
    const attrs = node.attrs ?? {};
    const title = (attrs.title as string | undefined) ?? "";
    if (attrs.linkType) return title;
    const key = attrs.entity_identifier as string | undefined;
    return key && values[key] != null ? values[key] : title;
  }
  return (node.content ?? [])
    .map((child) => nodeToText(child, values))
    .join("");
};

export const resolvePlainTextForRecipient = (
  json: unknown,
  values: Record<string, string>
): string => {
  const doc = json as DocNode | null | undefined;
  if (!doc) return "";
  const blocks = doc.type === "doc" ? (doc.content ?? []) : [doc];
  return blocks
    .map((block) => nodeToText(block, values))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const resolveHtmlForRecipient = (
  html: string,
  values: Record<string, string>
): string =>
  html.replace(
    /<mention-component\b([^>]*?)(?:\/>|><\/mention-component>)/gi,
    (_tag, attrs: string) => {
      const title = /\btitle="([^"]*)"/i.exec(attrs)?.[1] ?? "";
      if (/\bdata-link-type=/i.test(attrs))
        return title
          ? `<a href="${escapeHtml(title)}">${escapeHtml(title)}</a>`
          : "";
      const key = /\bentity_identifier="([^"]*)"/i.exec(attrs)?.[1] ?? "";
      const value = key && values[key] != null ? values[key] : title;
      return value ? escapeHtml(value) : "";
    }
  );
